<template>
  <div class="audio-recorder border rounded-lg p-3 bg-gray-50 flex flex-col gap-3">
    <div class="flex items-center gap-3">
        <!-- Record Control -->
        <Button
            v-if="!isRecording && !audioBlob"
            icon="pi pi-microphone"
            class="p-button-rounded p-button-danger p-button-outlined"
            @click="startRecording"
            v-tooltip="'Start Recording'"
        />

        <Button
            v-if="isRecording"
            icon="pi pi-stop"
            class="p-button-rounded p-button-danger"
            @click="stopRecording"
            v-tooltip="'Stop Recording'"
        />

        <div v-if="isRecording" class="text-red-600 animate-pulse font-mono flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-red-600"></span>
            {{ formattedDuration }}
        </div>

        <!-- Review Control -->
        <div v-if="audioBlob && !uploading" class="flex items-center gap-3 w-full">
            <audio ref="audioPlayer" controls :src="audioUrl" class="h-8 w-full max-w-[200px]"></audio>

            <Button
                icon="pi pi-check"
                class="p-button-rounded p-button-sm"
                @click="confirmUpload"
                v-tooltip="'Use Recording'"
                v-if="!uploadedId"
            />
             <div v-else class="flex items-center gap-1 text-primary text-sm font-medium">
                 <i class="pi pi-check-circle"></i> Ready
            </div>

            <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-secondary p-button-text p-button-sm"
                @click="resetRecording"
                v-tooltip="'Discard'"
            />
        </div>

         <div v-if="uploading" class="flex items-center gap-2 text-blue-600 text-sm">
             <i class="pi pi-spin pi-spinner"></i> Uploading...
        </div>
    </div>
    <div v-if="error" class="text-xs text-red-500">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
import RecordRTC from 'recordrtc';
import { useFileUpload } from '@/composables/common/useFileUpload';
import { useToast } from 'primevue/usetoast';

const emit = defineEmits(['recorded']);

const isRecording = ref(false);
const recorder = ref<RecordRTC | null>(null);
const audioBlob = ref<Blob | null>(null);
const audioUrl = ref<string>('');
const startTime = ref<number>(0);
const duration = ref(0);
const timerInterval = ref<ReturnType<typeof setInterval> | null>(null);
const error = ref('');
const uploadedId = ref<string | null>(null);

const { uploadFiles, uploading } = useFileUpload();
const toast = useToast();

const formattedDuration = computed(() => {
  const m = Math.floor(duration.value / 60).toString().padStart(2, '0');
  const s = (duration.value % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
});

const startRecording = async() => {
  error.value = '';
  resetState();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recorder.value = new RecordRTC(stream, {
      type: 'audio',
      mimeType: 'audio/webm', // or audio/mp3 if supported
      recorderType: RecordRTC.StereoAudioRecorder,
      desiredSampRate: 16000,
    });

    recorder.value.startRecording();
    isRecording.value = true;
    startTime.value = Date.now();
    timerInterval.value = setInterval(() => {
      duration.value = Math.floor((Date.now() - startTime.value) / 1000);
    }, 1000);

  } catch (err: unknown) {
    error.value = 'Could not access microphone. Please allow permissions.';
  }
};

const stopRecording = () => {
  if (recorder.value) {
    recorder.value.stopRecording(() => {
      audioBlob.value = recorder.value.getBlob();
      audioUrl.value = URL.createObjectURL(audioBlob.value!);

      // Stop stream tracks
      const stream = recorder.value.getInternalRecorder().stream; // workaround to access stream if needed?
      // Actually RecordRTC usually handles this, or we should access the stream passed in.
      // Better: use the stream we got earlier, but we didn't save it to ref.
      // RecordRTC stops tracks usually? let's double check doc/behavior.
      // Safe bet: accessing internal recorder to stop tracks if needed, or assume browser handles gc.
    });
    isRecording.value = false;
    clearInterval(timerInterval.value);
  }
};

const resetState = () => {
  audioBlob.value = null;
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
  audioUrl.value = '';
  duration.value = 0;
  uploadedId.value = null;
  emit('recorded', null); // emit removal
};

const resetRecording = () => {
  resetState();
};

const confirmUpload = async() => {
  if (!audioBlob.value) return;

  // Convert blob to File
  const file = new File([audioBlob.value], `audio-record-${Date.now()}.webm`, { type: 'audio/webm' });

  try {
    const ids = await uploadFiles([file]);
    if (ids && ids.length > 0) {
      uploadedId.value = ids[0];
      emit('recorded', uploadedId.value);
      toast.add({ severity: 'success', summary: 'Attached', detail: 'Audio attached.' });
    }
  } catch (e) {
    // handled in composable
  }
};

onUnmounted(() => {
  if (timerInterval.value) clearInterval(timerInterval.value);
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
});
</script>

<style scoped>
audio::-webkit-media-controls-panel {
    background-color: transparent;
}
</style>
